import { RefObject, useLayoutEffect } from 'react';

// Aspetto della pagina pubblica di un cantiere: tema (logo, copertina, due colori) ed
// eventuale modello scelto dalla libreria. Le forme sono quelle del contratto con il backend.

export interface TemaPagina {
  colorePrincipale: string | null;
  coloreSecondario: string | null;
  logoImpresa: string | null;
  copertina: string | null;
}
export interface ModelloRender { html: string; css: string }
export interface AspettoPagina { modello: ModelloRender | null; tema: TemaPagina }

const COLORE = /^#[0-9a-f]{6}$/i;
// I colori arrivano dal server ma finiscono dentro un foglio di stile: si accetta solo #rrggbb.
export const coloreValido = (c: string | null | undefined): c is string => typeof c === 'string' && COLORE.test(c);

const canali = (hex: string): [number, number, number] =>
  [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const aEsadecimale = (c: [number, number, number]): string =>
  `#${c.map(n => n.toString(16).padStart(2, '0')).join('')}`;

// Tinta più scura per l'hover dei pulsanti col colore scelto.
export function scurisci(hex: string, quota = 0.15): string {
  return aEsadecimale(canali(hex).map(n => Math.round(n * (1 - quota))) as [number, number, number]);
}

// Colore del testo sopra uno sfondo: bianco o blu notte, a seconda di quale si legge meglio (WCAG).
export function testoSu(hex: string): string {
  const [r, g, b] = canali(hex).map(n => {
    const v = n / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  const luminanza = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminanza <= 0.1791 ? '#ffffff' : '#0f172a';
}

export const VARIABILI_TEMA = ['--colore-principale', '--colore-principale-hover', '--colore-principale-testo', '--colore-secondario'] as const;
export type NomeVariabileTema = typeof VARIABILI_TEMA[number];

// Solo le variabili dei colori impostati (e validi): le altre restano ai valori standard di BOP.
export function variabiliTema(principale: string | null | undefined, secondario: string | null | undefined): Partial<Record<NomeVariabileTema, string>> {
  const v: Partial<Record<NomeVariabileTema, string>> = {};
  if (coloreValido(principale)) {
    v['--colore-principale'] = principale.toLowerCase();
    v['--colore-principale-hover'] = scurisci(principale);
    v['--colore-principale-testo'] = testoSu(principale);
  }
  if (coloreValido(secondario)) v['--colore-secondario'] = secondario.toLowerCase();
  return v;
}

// Regola :host del modello (Shadow DOM): colori del tema oppure quelli standard di BOP.
export function cssHost(principale: string | null | undefined, secondario: string | null | undefined): string {
  const v = variabiliTema(principale, secondario);
  const dichiarazioni = [
    'display:block',
    `--colore-principale:${v['--colore-principale'] ?? 'var(--bop-color-primary)'}`,
    `--colore-secondario:${v['--colore-secondario'] ?? 'var(--bop-color-brand)'}`,
  ];
  if (v['--colore-principale-hover']) dichiarazioni.push(`--colore-principale-hover:${v['--colore-principale-hover']}`);
  if (v['--colore-principale-testo']) dichiarazioni.push(`--colore-principale-testo:${v['--colore-principale-testo']}`);
  return `:host{${dichiarazioni.join(';')}}`;
}

// Imposta le variabili dei colori sul contenitore con setProperty (niente style={{}}); senza colori le toglie.
// Effect di layout: i colori sono già quelli giusti al primo disegno, senza un istante coi colori standard.
export function useVariabiliTema(ref: RefObject<HTMLElement | null>, tema: TemaPagina | null | undefined) {
  const principale = tema?.colorePrincipale ?? null;
  const secondario = tema?.coloreSecondario ?? null;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const variabili = variabiliTema(principale, secondario);
    VARIABILI_TEMA.forEach(nome => {
      const valore = variabili[nome];
      if (valore) el.style.setProperty(nome, valore);
      else el.style.removeProperty(nome);
    });
  }, [ref, principale, secondario]);
}
